import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { translate, getStyles, localizeDateTime } from '../../../../shared/utility';
import { isDate, unsetTime } from '../DateTimeHelpers';

import Button from '../../Button/Button';

import localStyles from './DatePicker.scss';
const styles = getStyles(localStyles);

class DatePicker extends Component {
    constructor(props) {
        super(props);

        this.today = unsetTime(new Date());

        this.state = {
            visibleMonth: props.startValue !== null ? props.startValue : this.today,

            start: unsetTime(props.startValue),
            end: unsetTime(props.endValue),

            min: isDate(props.minDate) ? unsetTime(props.minDate) : props.minDate === 'today' ? this.today : null,
            max: isDate(props.maxDate) ? unsetTime(props.maxDate) : props.maxDate === 'today' ? this.today : null
        };
    }

    componentDidMount(){

    }

    componentDidUpdate(prevProps, prevState){
        const newState = {};
        if(prevProps.startValue !== this.props.startValue){
            newState.start = unsetTime(this.props.startValue);
        }
        if(prevProps.endValue !== this.props.endValue){
            newState.end = unsetTime(this.props.endValue);
        }

        if(prevState.start - this.state.start){
            this.props.select(this.state.start, 'date', 'start');
        }
        if(prevState.end - this.state.end){
            this.props.select(this.state.end, 'date', 'end');
        }

        if(Object.keys(newState).length){
            this.setState(newState);
        }
    }

    /* * * * * * * * * *
     * EVENT HANDLERS  *
     * * * * * * * * * */
    selectDate = (date) => {
        const newState = {
            start: isDate(this.state.start) ? this.state.start : null,
            end: isDate(this.state.end) ? this.state.end : null
        };

        newState[this.props.focus] = (date - newState[this.props.focus]) ? date : null;

        if (this.props.isRange && this.datesAreFilledOut() && newState.start >= newState.end) {
            newState.start = date;
            newState.end = date;
        }

        this.props.focusNext();
        
        if(Object.keys(newState).length){
            this.setState(newState);
        }
    };

    /* * * * * * * *
     * TOGGLE VIEW *
     * * * * * * * */
    switchMonth = (diff) => this.setState({
        visibleMonth: new Date(
            this.state.visibleMonth.getFullYear(),
            this.state.visibleMonth.getMonth() + diff,
            1, 0, 0, 0, 0
        )
    });
    switchToNextMonth = () => this.switchMonth(1);
    switchToPrevMonth = () => this.switchMonth(-1);

    /* * * * * * * * * *
     * CUSTOM METHODS  *
     * * * * * * * * * */
    datesAreFilledOut = () => isDate(this.state.start) && (!this.props.isRange || isDate(this.state.end));

    // is date between min and max dates
    // and is within min and max range if applicable
    isDateValid = (date) => (this.state.min === null || date >= this.state.min) && (this.state.max === null || date <= this.state.max) && this.isDateInSelectableRange(date);

    // if range picker: check if date is within max and min range
    isDateInSelectableRange = (date) => {
        if(!this.props.isRange || (this.isDateSelectedStartDate(date) && this.props.focus === 'start') || (this.isDateSelectedEndDate(date) && this.props.focus === 'end')){
            return true;
        }

        const minDate = isDate(this.state.start) && this.props.focus === 'end' ?
            unsetTime(new Date(this.state.start.getTime() + this.props.minRange)) :
            isDate(this.state.end) && this.props.focus === 'start' ?
                unsetTime(new Date(this.state.end.getTime() - this.props.maxRange)) : false;
        const maxDate = isDate(this.state.start) && this.props.focus === 'end' ?
            unsetTime(new Date(this.state.start.getTime() + this.props.maxRange)) :
            isDate(this.state.end) && this.props.focus === 'start' ?
                unsetTime(new Date(this.state.end.getTime() - this.props.minRange)) : false;

        return (minDate && maxDate) ? (date >= minDate && date <= maxDate) : minDate ? date >= minDate : maxDate ? date <= maxDate : true;
    }

    // is date selected (either start or end)
    isDateSelectedStartDate = (date) => !(date - this.state.start);
    isDateSelectedEndDate = (date) => !(date - this.state.end);

    // is date between selected start and end date
    isDateInSelectedRange = (date) => this.props.isRange && this.datesAreFilledOut() && date >= this.state.start && date <= this.state.end;

    // is date either first or last selected date
    isDateFirstInSelectedRange = (date) => this.props.isRange && this.datesAreFilledOut() && this.isDateSelectedStartDate(date);
    isDateLastInSelectedRange = (date) => this.props.isRange && this.datesAreFilledOut() && this.isDateSelectedEndDate(date);

    // is date today
    isDateToday = (date) => !(date - this.today);

    /* * * * * * * * * * * *
     * CREATE THE CALENDAR *
     * * * * * * * * * * * */
    getCalendar = (year, month) => {
        const calendar = [];
        const weekdays = [];

        let currentDate = new Date(year, month, 1, 0, 0, 0, 0);

        while (currentDate.getMonth() === month || calendar.length < 6) {
            const week = (new Array(7)).fill(0);
            let day = !currentDate.getDay() ? 6 : currentDate.getDay() - 1;
            while (day < 7 && currentDate.getMonth() === month) {
                const date = new Date(currentDate);
                week[day] = {
                    date: date,
                    isInvalid: !this.isDateValid(date),
                    isSelected: this.isDateSelectedStartDate(date) || this.isDateSelectedEndDate(date),
                    isInSelectedRange: this.isDateInSelectedRange(date),
                    isFirstInSelectedRange: this.isDateFirstInSelectedRange(date),
                    isLastInSelectedRange: this.isDateLastInSelectedRange(date),
                    isToday: this.isDateToday(date)
                };
                weekdays[day] = weekdays[day] || localizeDateTime(date, this.props.locale, { weekday: 'short', year: undefined, month: undefined, day: undefined });
                day++;
                currentDate.setDate(currentDate.getDate() + 1);
            }
            calendar.push(week);
        }

        return (
            <table className={styles('table', 'c--text-darker', 'align--center')}>
                <thead>
                    <tr className={styles('table__row')}>
                        {weekdays.map((day, index) => (
                            <th key={index} className={styles('table__cell', 'table__cell--head', 'align--center')}>
                                <span className={styles('day', 'day--weekday', 'w--400')}>{day}</span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {calendar.map((week, index) => (
                        <tr key={index} className={styles('table__row')}>
                            {week.map((day, index) => (
                                <td key={index} className={styles(
                                    'table__cell',
                                    day.isInSelectedRange ? 'table__cell--inrange' : '',
                                    day.isFirstInSelectedRange ? 'table__cell--inrange--first' : '',
                                    day.isLastInSelectedRange ? 'table__cell--inrange--last' : ''
                                )}>
                                    {!day ? null : (
                                        <span className={styles(
                                            'day', 'day--date',
                                            day.isInvalid ? 'day--date--invalid' : 'day--date--valid',
                                            day.isInvalid && !day.isInSelectedRange ? 'c--text-lightest' : '',
                                            day.isSelected ? ['day--date--selected', 'b--brand'] : '',
                                            day.isInSelectedRange ? 'day--date--inrange' : '',
                                            day.isToday ? ['day--date--today', 'w--500'] : '',
                                        )}>
                                            <Button
                                                classes={styles('day__button', day.isInvalid ? 'day__button--invalid' : '')}
                                                modifiers={['text']}
                                                events={day.isInvalid ? {} : {click:()=>this.selectDate(day.date)}}
                                            >
                                                {day.date.getDate()}
                                            </Button>
                                        </span>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    /* * * * * *
     * RENDER  *
     * * * * * */
    render() {
        return (
            <div className={styles('datepicker', this.props.isOpen ? 'datepicker--open' : '')}>
                <div className={styles('datepicker__arrow')}></div>
                <div className={styles('datepicker__wrapper', 'b--background')}>
                    <div className={styles('datepicker__header', 'flex', 'flex--justify-between', 'flex--align-center', 'align--center')}>
                        <div className={styles('datepicker__header__button')}>
                            <Button modifiers={['xs', 'square', 'white', 'border']} icon={'arrow-left'} events={{ click: this.switchToPrevMonth }} />
                        </div>
                        <h5 className={styles('datepicker__header__title')}>{localizeDateTime(this.state.visibleMonth, this.props.locale, { month: 'long', year: 'numeric' })}</h5>
                        <div className={styles('datepicker__header__button')}>
                            <Button modifiers={['xs', 'square', 'white', 'border']} icon={'arrow-right'} events={{ click: this.switchToNextMonth }} />
                        </div>
                    </div>
                    {this.getCalendar(this.state.visibleMonth.getFullYear(), this.state.visibleMonth.getMonth())}
                    <div className={styles('datpicker__footer', 'align--right')}>
                        <Button modifiers={['primary']} events={{click: this.props.close}}>{translate('ui.apply', this.props.language)}</Button>
                    </div>
                </div>
            </div>
        );
    }
}

DatePicker.propTypes = {
    isRange: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired,

    focus: PropTypes.oneOf(['start', 'end']).isRequired,

    startValue: PropTypes.instanceOf(Date),
    endValue: PropTypes.instanceOf(Date),
    minDate: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.oneOf(['today'])
    ]),
    maxDate: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.oneOf(['today'])
    ]),
    minRange: PropTypes.number.isRequired,
    maxRange: PropTypes.number.isRequired,

    focusNext: PropTypes.func.isRequired,
    select: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,

    locale: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired
};

export default DatePicker;



// WEBPACK FOOTER //
// ./src/components/UI/DateTimePicker/DatePicker/DatePicker.js